import { Show } from "@/components/admin"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

import { Progress } from "@/components/ui/progress"

import { Separator } from "@/components/ui/separator"
import { AlertTriangle, Box, Leaf, ShieldCheck } from "lucide-react"
import { useShowContext } from "ra-core"
import type { ComponentType, ReactElement, ReactNode } from "react"

import type { MaterialRecord } from "./materials.types"

type MaterialInsightRecord = MaterialRecord & {
  eco_score?: number | null
  toxicity_score?: number | null
  certifications?: string[] | null
  preview_image_url?: string | null
}

type SustainabilityBadge = {
  icon: ComponentType<{ className?: string }>
  label: string
  description: string
  variant: "default" | "secondary" | "destructive" | "outline"
}

const MaterialShow = (): ReactElement => {
  return (
    <Show title="">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <MaterialInsightCard />
        <MaterialDetailsPanel />
      </div>
    </Show>
  )
}

const MaterialInsightCard = (): ReactElement | null => {
  const { record } = useShowContext<MaterialInsightRecord>()

  if (!record) {
    return null
  }

  const costPerUnit = getCostPerUnit(record)
  const carbonFootprint = record.carbon_footprint ?? null
  const ecoScore = computeEcoScore(record, carbonFootprint)
  const toxicityScore = computeToxicityScore(record)
  const carbonScore =
    carbonFootprint !== null
      ? clampScore(100 - normalizeCarbonFootprint(carbonFootprint))
      : 55
  const certifications = getCertificationList(record)
  const previewSrc =
    record.preview_image_url ??
    "https://images.unsplash.com/photo-1666462296991-45c5eb42067c?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfDM3fHx8ZW58MHx8fHx8"
  const supplierName = getSupplier(record)
  const metadataHighlights = getMetadataHighlights(record.metadata)
  const applications = record.applications?.filter(Boolean) ?? []

  const sustainabilityBadges: SustainabilityBadge[] = [
    {
      icon: Leaf,
      label: ecoScore >= 70 ? "Éco-conçu" : "Standard",
      description:
        ecoScore >= 70
          ? "Empreinte carbone sous contrôle"
          : "Optimisations possibles",
      variant: ecoScore >= 70 ? "secondary" : "outline",
    },
    {
      icon: AlertTriangle,
      label: toxicityScore <= 35 ? "Faible toxicité" : "Manipulation PPE",
      description:
        toxicityScore <= 35
          ? "Conforme aux directives VOC"
          : "Protection renforcée requise",
      variant: toxicityScore <= 35 ? "outline" : "destructive",
    },
    {
      icon: ShieldCheck,
      label: certifications[0] ?? "Certifications",
      description: "Audits qualité validés",
      variant: "default",
    },
  ]

  return (
    <Card className="relative overflow-hidden border-emerald-100/50 bg-gradient-to-br from-emerald-50 via-background to-background dark:border-emerald-900/40 dark:from-emerald-950/30">
      <CardHeader className="gap-4 lg:flex-row lg:items-end">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground">
            <span>{record.category ?? "Matériau"}</span>
            {record.type && (
              <span className="text-muted-foreground/80">· {record.type}</span>
            )}
          </div>
          <CardTitle className="text-3xl font-black leading-tight">
            {record.name}
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            {record.type && (
              <Badge
                variant="outline"
                className="rounded-full px-3 py-1 text-xs font-semibold uppercase"
              >
                {record.type}
              </Badge>
            )}
            {record.metadata?.grade && (
              <Badge
                variant="secondary"
                className="rounded-full px-3 py-1 text-xs font-semibold uppercase"
              >
                Grade {record.metadata.grade}
              </Badge>
            )}
          </div>
          <div className="space-y-1 text-sm text-muted-foreground">
            <p>Unité&nbsp;: {record.unit ?? "à préciser"}</p>
            <p>Fournisseur&nbsp;: {supplierName ?? "Non renseigné"}</p>
          </div>
        </div>
        <div className="rounded-2xl border border-emerald-200/60 bg-background/60 px-6 py-4 text-center shadow-sm">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
            Coût unitaire
          </p>
          <p className="text-2xl font-semibold">
            {formatCurrency(costPerUnit)}
          </p>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          <MetricCard
            title="Indice éco"
            value={`${ecoScore}%`}
            description="Score interne sur 100"
            intent={ecoScore >= 70 ? "positive" : "neutral"}
            progressValue={ecoScore}
          />
          <MetricCard
            title="Indice toxicité"
            value={`${toxicityScore}%`}
            description="Niveau de vigilance"
            intent={toxicityScore <= 35 ? "neutral" : "warning"}
            progressValue={toxicityScore}
          />
          <MetricCard
            title="Empreinte carbone"
            value={formatCarbonFootprint(carbonFootprint)}
            description="kg CO₂e / unité"
            intent={
              carbonFootprint !== null && carbonFootprint <= 40
                ? "positive"
                : carbonFootprint !== null && carbonFootprint > 80
                  ? "warning"
                  : "neutral"
            }
            progressValue={carbonScore}
          />
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium text-muted-foreground">
            Applications clés
          </p>
          <ApplicationsList
            applications={applications}
            emptyLabel="Non renseigné"
          />
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium text-muted-foreground">
            Indicateurs qualité
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {sustainabilityBadges.map(
              ({ icon: Icon, label, description, variant }) => (
                <div
                  key={label}
                  className="flex items-start gap-3 rounded-2xl border bg-background/60 p-3 shadow-sm"
                >
                  <Badge variant={variant} className="rounded-full p-2">
                    <Icon className="size-4" />
                  </Badge>
                  <div>
                    <p className="text-sm font-semibold">{label}</p>
                    <p className="text-xs text-muted-foreground">
                      {description}
                    </p>
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        {metadataHighlights.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground">
                Spécifications matière
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {metadataHighlights.slice(0, 4).map(({ label, value }) => (
                  <div
                    key={label}
                    className="rounded-2xl border bg-background/60 p-3 shadow-sm"
                  >
                    <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                      {label}
                    </p>
                    <p className="text-sm font-semibold">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <Separator />

        <div className="space-y-3">
          <p className="text-sm font-medium text-muted-foreground">
            Maquette 3D concept
          </p>
          <div className="relative overflow-hidden rounded-2xl border bg-muted/40">
            <img
              src={previewSrc}
              alt={`Rendu 3D du matériau ${record.name}`}
              className="aspect-video w-full object-cover"
              loading="lazy"
            />
            <Badge className="absolute left-4 top-4 gap-2 bg-background/80 text-foreground backdrop-blur">
              <Box className="size-4" /> Aperçu 3D
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            Visualisation indicative pour le suivi BIM/IFC. Remplacez par un
            rendu officiel dès qu’il est disponible.
          </p>
        </div>

        <Separator />

        <div className="space-y-3">
          <p className="text-sm font-medium text-muted-foreground">
            Certifications & accréditations
          </p>
          <div className="flex flex-wrap gap-2">
            {certifications.map(cert => (
              <Badge
                key={cert}
                variant="outline"
                className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide"
              >
                {cert}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const MaterialDetailsPanel = (): ReactElement | null => {
  const { record } = useShowContext<MaterialInsightRecord>()

  if (!record) {
    return null
  }

  const costPerUnit = getCostPerUnit(record)
  const carbonFootprint = record.carbon_footprint ?? null
  const supplierName = getSupplier(record)
  const metadataHighlights = getMetadataHighlights(record.metadata)

  return (
    <div className="space-y-4 rounded-2xl border bg-card/60 p-6 shadow-sm">
      <h3 className="text-lg font-semibold">Fiche technique</h3>
      <Separator />
      <div className="grid gap-4">
        <DetailRow label="Catégorie" value={record.category} />
        <DetailRow label="Type" value={record.type} />
        <DetailRow label="Unité" value={record.unit} />
        <DetailRow
          label="Applications"
          value={<ApplicationsList applications={record.applications} />}
        />
        <DetailRow label="Coût par unité" value={formatCurrency(costPerUnit)} />
        <DetailRow
          label="Empreinte carbone"
          value={formatCarbonFootprint(carbonFootprint)}
        />
        <DetailRow label="Fournisseur" value={supplierName} />
        <DetailRow label="Grade" value={record.metadata?.grade} />
        <DetailRow label="Créé le" value={formatDateValue(record.created_at)} />
      </div>

      {metadataHighlights.length > 0 && (
        <div className="space-y-3">
          <Separator />
          <p className="text-sm font-medium text-muted-foreground">
            Caractéristiques complémentaires
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {metadataHighlights.map(({ label, value }) => (
              <DetailRow key={`meta-${label}`} label={label} value={value} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const ApplicationsList = ({
  applications,
  emptyLabel = "Aucune",
}: {
  applications?: string[] | null
  emptyLabel?: string
}): ReactElement => {
  if (!applications || applications.length === 0) {
    return <span className="text-muted-foreground">{emptyLabel}</span>
  }

  return (
    <div className="flex flex-wrap gap-2">
      {applications.map(application => (
        <Badge
          key={application}
          variant="outline"
          className="rounded-full px-3 py-1 text-xs font-semibold"
        >
          {application}
        </Badge>
      ))}
    </div>
  )
}

const DetailRow = ({
  label,
  value,
}: {
  label: string
  value?: ReactNode
}): ReactElement => {
  const displayValue =
    value === null || value === undefined || value === "" ? (
      <span className="text-muted-foreground">Non renseigné</span>
    ) : (
      value
    )

  return (
    <div className="rounded-xl border border-border/60 bg-background/40 p-3">
      <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
        {label}
      </p>
      <div className="text-sm font-medium text-foreground">{displayValue}</div>
    </div>
  )
}

const MetricCard = ({
  title,
  value,
  description,
  intent,
  progressValue,
}: {
  title: string
  value: string
  description: string
  intent: "positive" | "warning" | "neutral"
  progressValue: number
}): ReactElement => {
  const intentClass =
    intent === "positive"
      ? "from-emerald-100/80 to-transparent dark:from-emerald-900/20"
      : intent === "warning"
        ? "from-amber-100/80 to-transparent dark:from-amber-900/20"
        : "from-muted/80 to-transparent"
  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br ${intentClass} p-4 shadow-sm`}
    >
      <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
        {title}
      </p>
      <p className="text-3xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
      <div className="mt-3">
        <Progress
          value={progressValue}
          className="h-2 overflow-hidden rounded-full"
        />
      </div>
    </div>
  )
}

const clampScore = (value: number | null | undefined): number => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 0
  }
  return Math.min(100, Math.max(0, Math.round(value)))
}

const normalizeCarbonFootprint = (value: number): number => {
  const min = 5
  const max = 150
  const clamped = Math.min(max, Math.max(min, value))
  return ((clamped - min) / (max - min)) * 100
}

const computeEcoScore = (
  record: MaterialInsightRecord,
  carbonFootprint: number | null
): number => {
  if (carbonFootprint !== null) {
    return clampScore(100 - normalizeCarbonFootprint(carbonFootprint))
  }
  if (record.eco_score !== null && record.eco_score !== undefined) {
    return clampScore(record.eco_score)
  }
  return deriveEcoScore(record)
}

const computeToxicityScore = (record: MaterialInsightRecord): number => {
  const fiberType = record.metadata?.fiber_type?.toLowerCase()
  if (fiberType?.includes("steel")) {
    return 25
  }
  if (fiberType?.includes("synthetic") || fiberType?.includes("resin")) {
    return 65
  }
  if (fiberType?.includes("organic")) {
    return 35
  }
  if (record.toxicity_score !== null && record.toxicity_score !== undefined) {
    return clampScore(record.toxicity_score)
  }
  return deriveToxicityScore(record)
}

const deriveEcoScore = (record: MaterialRecord): number => {
  if (!record.category) {
    return 58
  }
  if (record.category.toLowerCase().includes("recycl")) {
    return 82
  }
  if (record.category.toLowerCase().includes("béton")) {
    return 64
  }
  return 55
}

const deriveToxicityScore = (record: MaterialRecord): number => {
  if (!record.category) {
    return 32
  }
  if (record.category.toLowerCase().includes("solvant")) {
    return 72
  }
  if (record.category.toLowerCase().includes("peinture")) {
    return 61
  }
  return 28
}

const getCostPerUnit = (record: MaterialInsightRecord): number | null => {
  if (record.cost_per_unit !== null && record.cost_per_unit !== undefined) {
    return Number(record.cost_per_unit)
  }
  const legacyPrice = (record as Record<string, unknown>).price
  if (typeof legacyPrice === "number") {
    return legacyPrice
  }
  if (typeof legacyPrice === "string") {
    const parsed = Number.parseFloat(legacyPrice)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "Tarif à définir"
  }
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: Number.isInteger(value) && value < 10 ? 0 : 2,
  }).format(value)
}

const formatCarbonFootprint = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "Non communiqué"
  }
  return `${value.toFixed(2)} kg CO₂e`
}

const formatDateValue = (value?: string | null): string => {
  if (!value) {
    return "Non renseigné"
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return "Non renseigné"
  }
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "long",
  }).format(date)
}

const getSupplier = (record: MaterialInsightRecord): string | null => {
  return record.metadata?.supplier ?? record.supplier ?? null
}

const getMetadataHighlights = (
  metadata: MaterialRecord["metadata"]
): { label: string; value: string }[] => {
  if (!metadata) {
    return []
  }
  const labelMap: Record<string, string> = {
    fiber_type: "Type de fibre",
    finish: "Finition",
    density: "Densité",
    size: "Format",
    origin: "Origine",
    power: "Puissance",
  }

  return Object.entries(labelMap)
    .map(([key, label]) => {
      const rawValue = metadata[key]
      if (rawValue === null || rawValue === undefined || rawValue === "") {
        return null
      }
      return { label, value: String(rawValue) }
    })
    .filter((entry): entry is { label: string; value: string } =>
      Boolean(entry)
    )
}

const extractStringArray = (
  metadata: MaterialRecord["metadata"],
  key: string
): string[] | null => {
  if (!metadata) {
    return null
  }
  const value = metadata[key]
  if (Array.isArray(value)) {
    return value.map(item => String(item)).filter(Boolean)
  }
  if (typeof value === "string") {
    const parts = value.split(/[,;]/).map(item => item.trim())
    return parts.filter(Boolean)
  }
  return null
}

const getCertificationList = (record: MaterialInsightRecord): string[] => {
  if (record.certifications?.length) {
    return record.certifications
  }
  const metadataCerts =
    extractStringArray(record.metadata, "certifications") ??
    extractStringArray(record.metadata, "accreditations") ??
    extractStringArray(record.metadata, "iso")

  return metadataCerts?.length ? metadataCerts : ["ISO 14001", "ISO 45001"]
}

export default MaterialShow
